/**
 * isartau — Sichtbarkeits-Beobachter fuer die Produktbildgalerie (Etappe 0)
 * ---------------------------------------------------------------------------
 * Stellt den EINEN verlaesslichen Zeitpunkt bereit, an dem feststeht, welche
 * Medien der Galerie sichtbar sind. Aendert selbst nichts an der Optik.
 *
 * WARUM ES DIESE DATEI GIBT
 *
 * Zwei voneinander unabhaengige Mechanismen bestimmen zur Laufzeit, welche
 * Medien sichtbar sind:
 *
 *   1. Dawn. Weil templates/product.json auf hide_variants: true steht, liefert
 *      der Server je Variante eine andere Medienliste. assets/product-info.js
 *      (updateMedia) fuegt daraufhin <li>-Knoten ein, entfernt andere und
 *      sortiert um — echte childList-Mutationen.
 *
 *   2. Rubik Variant Images. Blendet anschliessend Medien aus, indem es BEIDE
 *      Marker setzt: Klasse "hidden" UND style="display:none". Es entfernt
 *      keine Knoten, feuert kein Ereignis und stellt keine API bereit
 *      (im Browser geprueft, siehe BEFUND-Produktbildgalerie_2026-08-15.md).
 *
 * Beim Produktwechsel mit Variantenmitnahme laufen beide nacheinander, und die
 * sichtbare Menge aendert sich DREIMAL — gemessen in Abstaenden von
 * 250 / 82 / 159 ms. Der erste Rubik-Durchlauf filtert dabei noch auf die
 * falsche Variante, weil die Mitnahme erst danach greift. Wer nach "dem" Umbau
 * rechnet, rechnet also womoeglich mitten in dieser Kette.
 *
 * Deshalb: beobachten, entprellen, und erst den eingependelten Zustand melden.
 *
 * WAS DIESE DATEI HINTERLAESST
 *
 *   <li data-galerie-position="1|2|3…">   nur auf SICHTBAREN Medien, ab 1,
 *                                          in DOM-Reihenfolge
 *   <media-gallery data-galerie-sichtbar="20"
 *                  style="--galerie-sichtbar: 20">
 *   Ereignis "isartau:galerie-eingependelt" am <media-gallery>, bubbles: true
 *
 * ---------------------------------------------------------------------------
 * SEMANTIK DES EREIGNISSES — fuer die Etappen 1 bis 3 verbindlich
 *
 * Das Ereignis feuert einmal je EINGEPENDELTEM ZUSTAND, nicht einmal je
 * Seitenaufruf. Aendert sich die sichtbare Menge nach dem Feuern erneut, wird
 * erneut entprellt und erneut gefeuert. Das Skript schaltet sich nie ab.
 *
 * Wer darauf hoert, muss seine Berechnung deshalb WIEDERHOLBAR (idempotent)
 * auslegen: aus dem gemeldeten Zustand den Sollzustand herstellen, nicht aus
 * dem vorigen Zustand fortschreiben. Ein Raster darf also nicht "die naechsten
 * vier Kacheln anhaengen", sondern muss "genau diese vier Kacheln setzen".
 *
 * Nicht gefeuert wird, wenn die sichtbare Menge nach dem Umbau dieselbe ist
 * wie davor — dann gibt es fuer die Etappen nichts neu zu rechnen.
 * ---------------------------------------------------------------------------
 *
 * Eingebunden ueber snippets/isartau-galerie-beobachter.liquid, aufgerufen aus
 * sections/main-product.liquid. Keine Dawn-Datei veraendert.
 */
(function () {
  'use strict';

  /* =========================================================================
   * Schalter
   * ======================================================================= */

  /**
   * Ruhezeit nach der LETZTEN beobachteten Aenderung, bevor gemeldet wird.
   *
   * Der Wert ist eine Messgroesse, keine Geschmacksfrage. Massgeblich ist der
   * Produktwechsel mit Variantenmitnahme: Dort liegt zwischen Rubiks erstem
   * Durchlauf und dem Anwenden der Mitnahme eine Luecke, in der NICHTS am DOM
   * geschieht — sie ueberbrueckt zwei Netzwerkanfragen nacheinander, erst
   * /products/<handle>.js der Mitnahme, dann Dawns Section Rendering.
   *
   * Im Staging ueber sechs Laeufe gemessen (tl-og-bur-flu -> tl-og-moo-flu):
   *
   *     59 · 72 · 75 · 212 · 362 · 489 ms
   *
   * Mit 300 ms feuerte das Ereignis in zwei dieser sechs Laeufe ZWEIMAL —
   * einmal auf dem Zwischenstand der Standardvariante, einmal auf dem
   * Endzustand. 600 ms deckt alle sechs Laeufe mit 111 ms Reserve.
   *
   * ACHTUNG, das ist ausdruecklich KEINE Garantie: Die Luecke haengt an der
   * Verbindung der Kundin, nicht am Code. Auf einer langsamen Mobilverbindung
   * kann sie groesser werden. Tritt das auf, wird hier erhoeht — und der neue
   * Wert gehoert wieder nachgemessen, nicht geschaetzt.
   *
   * Der naheliegende Einwand "das verzoegert das erste Bild" trifft NICHT zu:
   * Weil templates/product.json auf hide_variants: true steht, rendert Dawn
   * das Coverbild bereits serverseitig variantenrichtig. Die grosse obere
   * Kachel steht also sofort und ist der LCP; erst die kleineren Kacheln
   * ordnen sich nach der Meldung. Die Entprellungsdauer ist damit vom LCP
   * entkoppelt (Entscheidung der Inhaberin, 16.08.2026).
   */
  var ENTPRELLUNG_MS = 600;

  /** Konsolenausgaben. Im Normalbetrieb aus. */
  var DEBUG = false;

  /** Name des Ereignisses, an das sich die Etappen 1 bis 3 haengen. */
  var EREIGNIS = 'isartau:galerie-eingependelt';

  /** Position am sichtbaren Medium. */
  var ATTR_POSITION = 'data-galerie-position';

  /** Anzahl sichtbarer Medien an der Galerie. */
  var ATTR_ANZAHL = 'data-galerie-sichtbar';

  /** Dieselbe Anzahl als Custom Property, damit CSS damit rechnen kann. */
  var PROP_ANZAHL = '--galerie-sichtbar';

  /** Sperre gegen doppelte Registrierung (zweite Einbindung, Theme-Editor). */
  if (window.__isartauGalerieBeobachter) return;
  window.__isartauGalerieBeobachter = true;

  /* =========================================================================
   * Kleinkram
   * ======================================================================= */

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[galerie-beobachter]');
    console.log.apply(console, args);
  }

  /**
   * Ist dieses Medium sichtbar?
   *
   * Rubik blendet DOPPELT aus — classList.add('hidden') und
   * style.display = 'none' (assets von rubik-variant-images, theme_free.js).
   * Beide Marker werden geprueft, damit ein App-Update, das nur noch einen
   * davon setzt, nicht stillschweigend die Zaehlung verfaelscht.
   *
   * Bewusst NICHT ueber getComputedStyle oder offsetParent: beides erzwingt
   * ein Reflow. Bei 35 Medien und drei Umbauten je Produktwechsel waeren das
   * ueber hundert erzwungene Layoutberechnungen. classList.contains und
   * element.style lesen dagegen nur Attribute — kein Reflow.
   *
   * Preis dieser Entscheidung, bewusst in Kauf genommen: Ein Medium, das
   * ausschliesslich ueber eine Stylesheet-Regel verborgen waere, gaelte hier
   * als sichtbar. Im Layout "stacked" gibt es keine solche Regel — die
   * Thumbnail-Layouts, die eine haetten, sind nicht in Betrieb und duerfen es
   * auch nicht werden (siehe THEME-ANPASSUNGEN.md, Etappe 0).
   */
  function istSichtbar(li) {
    return !li.classList.contains('hidden') && li.style.display !== 'none';
  }

  /**
   * Klassenliste ohne die scroll-trigger-Anteile, sortiert.
   *
   * Dawns Einblend-Animation (settings.animations_reveal_on_scroll ist im Shop
   * AN) haengt und entfernt beim Scrollen fortlaufend
   * "scroll-trigger--offscreen" an denselben <li>. Allein beim Seitenaufbau
   * waren das 58 Klassenaenderungen. Ohne diesen Filter wuerde jede
   * Scrollbewegung die Entprellung neu aufziehen und die Meldung verzoegern.
   */
  function ohneScrollTrigger(wert) {
    return String(wert == null ? '' : wert)
      .split(/\s+/)
      .filter(function (klasse) {
        return klasse && klasse.indexOf('scroll-trigger') !== 0;
      })
      .sort()
      .join(' ');
  }

  /** Aenderung, die die Sichtbarkeit unmoeglich beeinflusst haben kann. */
  function istBelanglos(eintrag) {
    if (eintrag.type !== 'attributes') return false;
    if (eintrag.attributeName !== 'class') return false;
    return (
      ohneScrollTrigger(eintrag.oldValue) ===
      ohneScrollTrigger(eintrag.target.getAttribute('class'))
    );
  }

  /* =========================================================================
   * Auswerten und Schreiben
   * ======================================================================= */

  /**
   * Sichtbare Medien in DOM-Reihenfolge sammeln und daraus eine Signatur
   * bilden.
   *
   * Die Signatur ist die Liste der sichtbaren data-media-id, nicht deren
   * Anzahl. Das ist kein Feinschliff: Beim Wechsel Beschlaege gold -> silber
   * bleibt die ANZAHL bei 20, die MENGE wechselt vollstaendig. Eine Zaehlung
   * haette diesen Wechsel verschluckt.
   */
  function auswerten(liste) {
    var alle = liste.querySelectorAll('li[data-media-id]');
    var sichtbare = [];
    var teile = [];

    for (var i = 0; i < alle.length; i++) {
      if (!istSichtbar(alle[i])) continue;
      sichtbare.push(alle[i]);
      teile.push(alle[i].getAttribute('data-media-id'));
    }

    return { alle: alle, sichtbare: sichtbare, signatur: teile.join(',') };
  }

  /**
   * Nummerierung und Anzahl ans DOM schreiben.
   *
   * Geschrieben wird nur, wo sich der Wert tatsaechlich aendert. Das haelt die
   * Zahl der Attributschreibvorgaenge klein und macht die Aenderung in den
   * Entwicklerwerkzeugen lesbar, statt bei jedem Durchlauf alle 35 Knoten
   * aufblinken zu lassen.
   *
   * Das Schreiben loest den eigenen Beobachter NICHT aus: der lauscht ueber
   * attributeFilter ausschliesslich auf "class" und "style",
   * data-galerie-position steht nicht darin. Zusaetzlich faengt die
   * Signaturpruefung in melden() jede denkbare Rueckkopplung ab.
   */
  function schreiben(galerie, ergebnis) {
    var i;

    for (i = 0; i < ergebnis.alle.length; i++) {
      if (ergebnis.alle[i].hasAttribute(ATTR_POSITION)) {
        ergebnis.alle[i].removeAttribute(ATTR_POSITION);
      }
    }

    for (i = 0; i < ergebnis.sichtbare.length; i++) {
      ergebnis.sichtbare[i].setAttribute(ATTR_POSITION, String(i + 1));
    }

    var anzahl = String(ergebnis.sichtbare.length);
    if (galerie.getAttribute(ATTR_ANZAHL) !== anzahl) {
      galerie.setAttribute(ATTR_ANZAHL, anzahl);
      galerie.style.setProperty(PROP_ANZAHL, anzahl);
    }
  }

  /* =========================================================================
   * Zustand je Galerie
   * ======================================================================= */

  /**
   * Ein Eintrag je <media-gallery>. Auf der Produktseite gibt es genau eine;
   * die Liste haelt das Verhalten trotzdem eindeutig, falls spaeter eine
   * zweite Galerie auf derselben Seite steht (Etappe 2 prueft das).
   */
  var zustaende = [];

  function abraeumen() {
    zustaende.forEach(function (z) {
      try {
        z.beobachter.disconnect();
      } catch (fehler) {
        /* egal — der Knoten ist ohnehin fort */
      }
      window.clearTimeout(z.timer);
    });
    zustaende = [];
  }

  /**
   * Meldet den eingependelten Zustand — genau einmal je echter Aenderung.
   *
   * Die Signaturpruefung ist die Sperre gegen Mehrfachlaeufe. Sie greift in
   * zwei Faellen:
   *   - Scrollen und andere belanglose Attributaenderungen, die den Filter
   *     oben passiert haben,
   *   - der Umbau endet auf demselben Zustand, in dem er begonnen hat.
   * In beiden Faellen ist Schweigen richtig: die folgenden Etappen haben
   * nichts neu zu rechnen.
   */
  function melden(z) {
    if (!z.liste.isConnected) {
      log('Liste nicht mehr im Dokument, haenge neu auf');
      aufbauen();
      return;
    }

    var ergebnis = auswerten(z.liste);

    if (ergebnis.signatur === z.letzteSignatur) {
      log('unveraendert, keine Meldung');
      return;
    }

    z.letzteSignatur = ergebnis.signatur;
    schreiben(z.galerie, ergebnis);

    log('eingependelt:', ergebnis.sichtbare.length, 'von', ergebnis.alle.length);

    z.galerie.dispatchEvent(
      new CustomEvent(EREIGNIS, {
        bubbles: true,
        detail: {
          galerie: z.galerie,
          liste: z.liste,
          sichtbar: ergebnis.sichtbare.length,
          gesamt: ergebnis.alle.length,
          medien: ergebnis.sichtbare
        }
      })
    );
  }

  function anstossen(z) {
    window.clearTimeout(z.timer);
    z.timer = window.setTimeout(function () {
      try {
        melden(z);
      } catch (fehler) {
        log('Fehler beim Melden', fehler);
      }
    }, ENTPRELLUNG_MS);
  }

  /* =========================================================================
   * Aufbau
   * ======================================================================= */

  /**
   * Beobachter aufhaengen.
   *
   * AUFHAENGEPUNKT ist das <ul class="product__media-list">, nicht
   * <media-gallery>. Zwei Gruende, beide im Browser geprueft:
   *
   *   1. Der <ul> UEBERLEBT den Variantenwechsel. Dawns updateMedia arbeitet
   *      mit prepend, remove und insertBefore auf den KINDERN
   *      (assets/product-info.js). Der Beobachter reisst also nicht ab.
   *      Nachgemessen ueber Knotenidentitaet: <ul>, <media-gallery> und
   *      <product-info> waren nach dem Wechsel dieselben Objekte.
   *
   *   2. Der Badge-Stapel .vkf-stapel liegt AUSSERHALB. Er haengt als direktes
   *      Kind an <media-gallery>, zwischen #GalleryStatus und
   *      <slider-component> (snippets/product-media-gallery.liquid). Am <ul>
   *      aufgehaengt kann er weder faelschlich mitgezaehlt werden — die
   *      Zaehlung laeuft ohnehin nur ueber li[data-media-id] — noch kann sein
   *      Inline-Script (es setzt beim Variantenwechsel el.hidden am Badge) eine
   *      Sichtbarkeitsaenderung vortaeuschen. Waere <media-gallery> der
   *      Aufhaengepunkt, muesste beides eigens ausgefiltert werden.
   *
   * KONFIGURATION deckt beide Umbauarten ab:
   *   childList + subtree  -> Dawns Knotentausch
   *   attributes(class,style) -> Rubiks Ausblenden
   *   attributeOldValue    -> nur fuer den scroll-trigger-Filter noetig
   *
   * attributeFilter ist Pflicht, nicht Kosmetik: ohne ihn meldet der
   * Beobachter jede Attributaenderung im Teilbaum, und das Schreiben der
   * eigenen Positionen wuerde sich selbst ausloesen.
   */
  function aufbauen() {
    abraeumen();

    var galerien = document.querySelectorAll('media-gallery');

    for (var i = 0; i < galerien.length; i++) {
      var galerie = galerien[i];
      var liste = galerie.querySelector('ul.product__media-list');
      if (!liste) continue;

      var z = {
        galerie: galerie,
        liste: liste,
        letzteSignatur: null,
        timer: null,
        beobachter: null
      };

      z.beobachter = new MutationObserver(
        (function (zustand) {
          return function (eintraege) {
            for (var k = 0; k < eintraege.length; k++) {
              if (!istBelanglos(eintraege[k])) {
                anstossen(zustand);
                return;
              }
            }
          };
        })(z)
      );

      z.beobachter.observe(liste, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ['class', 'style']
      });

      zustaende.push(z);

      /* Erste Meldung anstossen, auch ohne jede Mutation.
         Ohne diesen Anstoss bliebe es still, wenn Rubik nichts auszublenden
         hat — dann gaebe es fuer die folgenden Etappen nie einen Startpunkt.
         Rubiks Durchlauf liegt bei 250 bis 270 ms nach dem Erscheinen der
         Galerie und zieht die Entprellung ohnehin neu auf; gemeldet wird also
         weiterhin genau einmal, danach. */
      anstossen(z);
    }

    log('aufgebaut:', zustaende.length, 'Galerie(n)');
  }

  /* =========================================================================
   * Start
   * ======================================================================= */

  function start() {
    try {
      if (!document.querySelector('media-gallery')) return;
      aufbauen();

      /* Dawn ersetzt bei einem Produktwechsel ueber die Variantenauswahl das
         komplette <product-info> oder sogar <main>
         (assets/product-info.js, handleSwapProduct). Der alte <ul> ist danach
         tot. Das neue <product-info> feuert beim Einhaengen
         "product-info:loaded" mit bubbles: true — das ist der Anlass, neu
         aufzuhaengen. Der Fall tritt heute nicht auf, weil data-product-url
         an den Optionsfeldern leer ist; er kann sich aber allein durch die
         Produktpflege im Shop einstellen. */
      document.addEventListener('product-info:loaded', function () {
        log('product-info:loaded — haenge neu auf');
        aufbauen();
      });

      /* Theme-Editor: Section neu geladen oder entfernt. */
      document.addEventListener('shopify:section:load', function (ereignis) {
        if (ereignis.target && !ereignis.target.querySelector('media-gallery')) return;
        log('shopify:section:load — haenge neu auf');
        aufbauen();
      });
      document.addEventListener('shopify:section:unload', function (ereignis) {
        if (ereignis.target && !ereignis.target.querySelector('media-gallery')) return;
        log('shopify:section:unload — raeume ab');
        abraeumen();
      });
    } catch (fehler) {
      /* Jeder Fehler fuehrt zu "nichts tun". Diese Datei aendert nichts an der
         Optik — faellt sie aus, sieht die Seite aus wie vorher. Sie darf unter
         keinen Umstaenden andere Skripte stoppen. */
      log('Start fehlgeschlagen', fehler);
    }
  }

  /* Das Snippet bindet die Datei mit defer ein, das DOM ist hier also fertig
     geparst. Die Abfrage ist die Absicherung fuer den Fall, dass die
     Einbindung spaeter einmal ohne defer erfolgt. Gleiches Muster wie
     assets/isartau-variantenmitnahme.js. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
